import { _ } from "../../polyglot/polyglot.js";
import {
	angleDownSolidIcon,
	angleUpSolidIcon,
	editRegularIcon,
	ellipsisIcon,
	xmarkIcon,
} from "../../elements/icons/index.js";
import { formatFullWithNoDecimals } from "../../helpers/currency-formatting-util.js";
import { Widget } from "@foretagsplatsen/widgetjs";
import FlipWidget from "../FlipWidget/FlipWidget.js";

/**
 * Widget to show an existing manual journal entry.
 *
 * It renders the entry's name as well as its transactions.
 * It also renders actions to interact with the entry (edit/delete).
 *
 * @param {ManualEntryRuleModel} manualEntryRuleModel -
 * @param {function} onEdit - Callback invoked when "Edit" is clicked
 * @param {function} onDelete - Callback invoked when "Delete" is clicked
 * @param {boolean} [readOnly] - Whether the actions must be hidden or not
 */
export default class ManualEntryViewer extends Widget {
	constructor({
		manualEntryRuleModel,
		onEdit = () => {},
		onDelete = () => {},
		readOnly,
	}) {
		super(...arguments);

		this._manualEntryRuleModel = manualEntryRuleModel;
		this._onEdit = onEdit;
		this._onDelete = onDelete;
		this._expanded = false;
		this._readOnly = readOnly;

		this._flipWidget = new FlipWidget({
			frontRenderable: this._renderCollapsedBodyOn.bind(this),
			backRenderable: this._renderExpandedBodyOn.bind(this),
		});
	}

	collapse() {
		this._expanded = false;
		this._flipWidget.showFront();
		this.update();
	}

	expand() {
		this._expanded = true;
		this._flipWidget.showBack();
		this.update();
	}

	renderContentOn(html) {
		html.div(
			{ class: "ManualJournalEntryViewer" },
			this._renderHeaderOn.bind(this),
			this._flipWidget,
		);
	}

	_renderHeaderOn(html) {
		html.div(
			{ class: "viewer-header" },
			html.div(
				{ class: "name", "data-test": "name" },
				this._manualEntryRuleModel.getName(),
			),
			html.div(
				{ class: "actions" },
				this._renderCollapseButtonOn.bind(this),
				this._renderActionsGroupOn.bind(this),
			),
		);
	}

	_renderCollapseButtonOn(html) {
		html.div(
			{
				class: "action",
				click: () => {
					this._expanded = !this._expanded;

					this._flipWidget.toggle();
					this.update();
				},
			},
			this._expanded ? angleDownSolidIcon : angleUpSolidIcon,
		);
	}

	_renderActionsGroupOn(html) {
		if (this._readOnly) return;
		const dropdownId = `menu-dropdown-${this._id}`;

		html.div(
			{
				class: "btn-group",
				role: "group",
			},
			html.div(
				{
					id: dropdownId,
					class: "actions-drawer dropdown-toggle",
					"data-test": "action-button",
					"data-toggle": "dropdown",
					"aria-haspopup": "true",
				},
				ellipsisIcon,
			),
			html.div(
				{
					class: "actions-menu dropdown-menu",
					"aria-labelledby": dropdownId,
				},
				this._renderActionMenuItemsOn(html),
			),
		);
	}

	_renderCollapsedBodyOn(html) {
		const value = this._manualEntryRuleModel.getValue();

		html.div(
			{ class: "collapsed", "data-test": "collapsed" },
			_("Value: {value}", {
				value: formatFullWithNoDecimals(value),
			}),
		);
	}

	_renderExpandedBodyOn(html) {
		const value = this._manualEntryRuleModel.getValue();
		const vatRate = this._manualEntryRuleModel.getVatRateValue();
		const paymentSpread = this._manualEntryRuleModel.getPaymentSpread();

		html.div(
			{ "data-test": "expanded-value" },
			`${_("Value")}: ${formatFullWithNoDecimals(value)}`,
		);

		html.div(
			{ "data-test": "expanded-vatRate" },
			`${_("Vat rate")}: ${vatRate}`,
		);

		html.ul(
			{ class: "transactions", "data-test": "expanded-paymentSpread" },
			html.li(
				{ class: "transactions-header" },
				html.span(
					html.div(
						{
							class: "transaction-label",
						},
						(html) => html.div(_("Payment Spread")),
					),
				),
			),

			paymentSpread.map((payment, index) =>
				this._paymentRenderer({
					payment,
					index: index + 1,
				}),
			),
		);
	}

	_paymentRenderer({ payment, index }) {
		return (html) => {
			html.li(
				{
					class: "transaction",
				},

				html.div(
					index === 1
						? `${_("Current month")}: ${payment}%`
						: `${_("Month")} ${index}: ${payment}%`,
				),
			);
		};
	}

	_renderActionMenuItemsOn(html) {
		return [
			html.div(
				{
					"data-test": "edit-action",
					class: "action-menu-item dropdown-item icon-and-text",
					click: this._onEdit,
				},
				editRegularIcon,
				_("Edit"),
			),
			html.div(
				{
					"data-test": "delete-action",
					class: "action-menu-item danger dropdown-item icon-and-text",
					click: this._onDelete,
				},
				xmarkIcon,
				_("Delete"),
			),
		];
	}
}
